import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { convert as htmlToText } from 'html-to-text';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Sparkles, Mail, Send, Copy, CheckCircle2, ExternalLink } from 'lucide-react';
import { FeatureUnavailableModal } from './modals/FeatureUnavailableModal';

const API = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8001") + "/api";

const IntroEmailComposer = ({ open, onClose, leadId, leadName, leadEmail }) => {
  const [loading, setLoading] = useState(false);
  const [emailData, setEmailData] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [copied, setCopied] = useState(false);
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const [featureDetails, setFeatureDetails] = useState(null);

  const generateEmail = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/leads/${leadId}/generate-intro-email`,
        {},
        { withCredentials: true }
      );
      
      const data = response.data;
      setEmailData(data);
      setSubject(data.email.subject);
      // Convert plain text with \n to HTML for Quill
      const htmlBody = data.email.body.replace(/\n/g, '<br/>');
      setBody(htmlBody);
      
      toast.success("Email draft generated!");
    } catch (error) {
      console.error('Generate email error:', error);
      
      // Check if it's a feature gate error
      const errorDetail = error.response?.data?.detail;
      
      if (errorDetail && typeof errorDetail === 'object' && errorDetail.error_code === 'AI_INTRO_MAIL_NOT_AVAILABLE_FOR_PLAN') {
        // Show feature unavailable modal
        setFeatureDetails({
          current_plan: errorDetail.plan || 'base',
          feature: errorDetail.feature || 'AI Intro Mail',
          required_plan: 'Tier 1 or higher'
        });
        setShowFeatureModal(true);
        onClose(); // Close the composer dialog
      } else {
        // Handle other errors
        const errorMsg = typeof errorDetail === 'string' ? errorDetail : (errorDetail?.message || "Failed to generate email");
        toast.error(errorMsg);
        if (errorMsg.includes("company profile not configured")) {
          toast.info("Please set up your company profile in Admin Panel first");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    // Convert HTML to well-formatted plain text for clipboard
    const plainText = htmlToText(body, {
      wordwrap: false,
      preserveNewlines: true,
      selectors: [
        { selector: 'p', options: { trailingLineBreaks: 2 } },
        { selector: 'br', format: 'lineBreak' },
        { selector: 'h1', options: { uppercase: false, trailingLineBreaks: 2 } },
        { selector: 'h2', options: { uppercase: false, trailingLineBreaks: 2 } },
        { selector: 'ul', options: { itemPrefix: '• ' } },
        { selector: 'ol', options: { itemPrefix: ' ' } },
        { selector: 'li', options: { trailingLineBreaks: 1 } },
        { selector: 'a', options: { ignoreHref: false } }
      ]
    });
    
    const fullEmail = `Subject: ${subject}\n\nTo: ${leadEmail}\n\n${plainText}`;
    navigator.clipboard.writeText(fullEmail);
    setCopied(true);
    toast.success("Email copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const openInEmailClient = () => {
    if (!leadEmail) {
      toast.error("Lead email address is missing");
      return;
    }
    
    // Convert HTML to well-formatted plain text for mailto
    // Preserves line breaks, spacing, lists, and visual structure
    const plainText = htmlToText(body, {
      wordwrap: false,
      preserveNewlines: true,
      selectors: [
        { selector: 'p', options: { trailingLineBreaks: 2 } },
        { selector: 'br', format: 'lineBreak' },
        { selector: 'h1', options: { uppercase: false, trailingLineBreaks: 2 } },
        { selector: 'h2', options: { uppercase: false, trailingLineBreaks: 2 } },
        { selector: 'ul', options: { itemPrefix: '• ' } },
        { selector: 'ol', options: { itemPrefix: ' ' } },
        { selector: 'li', options: { trailingLineBreaks: 1 } },
        { selector: 'a', options: { ignoreHref: false } },
        { selector: 'strong', format: 'inline' },
        { selector: 'b', format: 'inline' },
        { selector: 'em', format: 'inline' },
        { selector: 'i', format: 'inline' }
      ]
    });
    
    const mailtoLink = `mailto:${leadEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(plainText)}`;
    window.location.href = mailtoLink;
    toast.success("Opening email client...");
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, false] }],
      ['bold', 'italic', 'underline'],
      [{'list': 'ordered'}, {'list': 'bullet'}],
      ['link'],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline',
    'list', 'bullet',
    'link'
  ];

  return (
    <>
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-indigo-600" />
            Tailored Intro Email for {leadName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {!emailData ? (
            <div className="text-center py-12">
              <Mail className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 mb-4">
                Generate a personalized introduction email based on your company profile
              </p>
              <Button
                onClick={generateEmail}
                disabled={loading}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Generating Email...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate AI-Powered Email
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Email Recipient Info */}
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-600">To:</p>
                    <p className="font-medium text-slate-900">{leadName}</p>
                    <p className="text-slate-700">{leadEmail}</p>
                  </div>
                  <div>
                    <p className="text-slate-600">From:</p>
                    <p className="font-medium text-slate-900">{emailData.sender.name}</p>
                    <p className="text-slate-700">{emailData.sender.email}</p>
                  </div>
                </div>
              </div>

              {/* Subject Line */}
              <div>
                <Label htmlFor="subject">Subject Line</Label>
                <Input
                  id="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="mt-1"
                  placeholder="Email subject"
                />
              </div>

              {/* Email Body - Rich Text Editor */}
              <div>
                <Label>Email Body</Label>
                <div className="mt-1 border border-slate-300 rounded-lg overflow-hidden">
                  <ReactQuill
                    theme="snow"
                    value={body}
                    onChange={setBody}
                    modules={modules}
                    formats={formats}
                    placeholder="Write your email here..."
                    className="bg-white"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3 pt-4 border-t">
                <Button
                  onClick={openInEmailClient}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                  disabled={!leadEmail}
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send via Email Client
                </Button>
                
                <Button
                  onClick={copyToClipboard}
                  variant="outline"
                  className="flex-1"
                >
                  {copied ? (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2 text-green-600" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy to Clipboard
                    </>
                  )}
                </Button>

                <Button
                  onClick={generateEmail}
                  variant="outline"
                  disabled={loading}
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Regenerate
                </Button>
              </div>

              {/* Help Text */}
              <div className="text-xs text-slate-500 bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="flex items-center gap-2">
                  <ExternalLink className="w-3 h-3" />
                  <strong>Tip:</strong> Edit the email as needed, then click "Send via Email Client" to open in your default email app (Outlook, Gmail, etc.)
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
    
    {/* Feature Unavailable Modal */}
    <FeatureUnavailableModal
      isOpen={showFeatureModal}
      onClose={() => setShowFeatureModal(false)}
      featureDetails={featureDetails}
    />
  </>
  );
};

export default IntroEmailComposer;
